import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import SortableTable from '../SortableTable';
import { useDN } from '../../contexts/DNContext';
import DNAbsenceReasons from './DNAbsenceReasons';
import DNAbsenceReasonGraph from './DNAbsenceReasonGraph';
import StatTile from '../ui/StatTile';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import { RiStore2Line, RiBox3Line, RiListCheck2, RiAlertLine, RiCheckboxCircleLine, RiFilterOffLine, RiFilterLine } from 'react-icons/ri';
import emitter from '../../emitter';
import IAAnalysis from '../IAAnalysis';
import { _t, _n } from '../../utils/i18n';

// Définir les couleurs de base
const BASE_COLORS = {
  backgrounds: [
    '#3B82F6', // blue-500
    '#10B981', // emerald-500
    '#F59E0B', // amber-500
    '#EF4444', // red-500
    '#8B5CF6', // violet-500
    '#EC4899', // pink-500
    '#6366F1', // indigo-500
    '#14B8A6', // teal-500
  ],
  borders: [
    '#2563EB', // blue-600
    '#059669', // emerald-600
    '#D97706', // amber-600
    '#DC2626', // red-600
    '#7C3AED', // violet-600
    '#DB2777', // pink-600
    '#4F46E5', // indigo-600
    '#0D9488', // teal-600
  ],
  backgroundsWithOpacity: [
    '#3B82F640', // blue-500 avec transparence
    '#10B98140', // emerald-500 avec transparence
    '#F59E0B40', // amber-500 avec transparence
    '#EF444440', // red-500 avec transparence
    '#8B5CF640', // violet-500 avec transparence
    '#EC489940', // pink-500 avec transparence
    '#6366F140', // indigo-500 avec transparence
    '#14B8A640', // teal-500 avec transparence
  ]
};

// Ajouter de nouvelles palettes de couleurs en haut du fichier après BASE_COLORS
const SHOP_SIGN_COLORS = {
  backgrounds: [
    '#6366F1', // indigo-500
    '#8B5CF6', // violet-500
    '#A855F7', // purple-500
    '#D946EF', // fuchsia-500
    '#EC4899', // pink-500
    '#F43F5E', // rose-500
    '#EF4444', // red-500
    '#F97316', // orange-500
  ],
  borders: [
    '#4F46E5', // indigo-600
    '#7C3AED', // violet-600
    '#9333EA', // purple-600
    '#C026D3', // fuchsia-600
    '#DB2777', // pink-600
    '#E11D48', // rose-600
    '#DC2626', // red-600
    '#EA580C', // orange-600
  ],
  backgroundsWithOpacity: [
    '#6366F140', // avec transparence
    '#8B5CF640',
    '#A855F740',
    '#D946EF40',
    '#EC489940',
    '#F43F5E40',
    '#EF444440',
    '#F9731640',
  ]
};

const PRODUCT_COLORS = {
  backgrounds: [
    '#10B981', // emerald-500
    '#34D399', // emerald-400
    '#14B8A6', // teal-500
    '#2DD4BF', // teal-400
    '#06B6D4', // cyan-500
    '#22D3EE', // cyan-400
    '#0EA5E9', // sky-500
    '#38BDF8', // sky-400
  ],
  borders: [
    '#059669', // emerald-600
    '#10B981', // emerald-500
    '#0D9488', // teal-600
    '#14B8A6', // teal-500
    '#0891B2', // cyan-600
    '#06B6D4', // cyan-500
    '#0284C7', // sky-600
    '#0EA5E9', // sky-500
  ],
  backgroundsWithOpacity: [
    '#10B98140', // avec transparence
    '#34D39940',
    '#14B8A640',
    '#2DD4BF40',
    '#06B6D440',
    '#22D3EE40',
    '#0EA5E940',
    '#38BDF840',
  ]
};

const DNErrorViewer = ({ selectedShopSigns, selectedCategories, currentUser, fetchWithAuth, projectId, obfuscate }) => {
  const {
    issues,
    issuesLoading: loading,
    issuesError: error,
    issuesPreviousPeriodStats,
    presetType,
    presetValue,
    startDate,
    endDate,
  } = useDN();

  const [reasonColorMapping, setReasonColorMapping] = useState(null);

  const [selectedReasons, setSelectedReasons] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showOnlyNewIssues, setShowOnlyNewIssues] = useState(true);

  // Style pour les selects
  const selectStyles = useSelectStyles();

  // Options pour les filtres
  const reasonOptions = useMemo(() => {
    if (!issues.length) return [];

    const uniqueReasons = [...new Set(issues.map(issue => issue.reason || 'Non catégorisé'))]
      .sort()
      .map(reason => ({ value: reason, label: reason }));

    return uniqueReasons;
  }, [issues]);

  const productOptions = useMemo(() => {
    if (!issues.length) return [];

    const uniqueProducts = [...new Set(issues.map(issue => issue.productName))]
      .filter(Boolean)
      .sort()
      .map(product => ({ value: product, label: product }));

    return uniqueProducts;
  }, [issues]);

  // Filtrer d'abord par enseigne
  const filteredByShopIssues = useMemo(() => {
    if (!selectedShopSigns || !selectedShopSigns.length) return issues;
    const selectedValues = selectedShopSigns.map(option => option.value);
    return issues.filter(issue => selectedValues.includes(issue.shopSign));
  }, [issues, selectedShopSigns]);

  // Puis filtrer par catégorie
  const filteredIssues = useMemo(() => {
    if (!selectedCategories || !selectedCategories.length) return filteredByShopIssues;
    const selectedValues = selectedCategories.map(option => option.value);
    return filteredByShopIssues.filter(issue =>
      issue.category && selectedValues.includes(issue.category)
    );
  }, [filteredByShopIssues, selectedCategories]);

  // Puis filtrer par raison, en démasquant tout si toutes les raisons sont masquées
  const fullyFilteredIssues = useMemo(() => {
    let result = filteredIssues;

    // Filtrer par raison si des raisons sont sélectionnées
    if (selectedReasons.length > 0) {
      const selectedReasonValues = selectedReasons.map(option => option.value);
      result = result.filter(issue =>
        selectedReasonValues.includes(issue.reason || 'Non catégorisé')
      );
    }

    // Filtrer par produit si des produits sont sélectionnés
    if (selectedProducts.length > 0) {
      const selectedProductValues = selectedProducts.map(option => option.value);
      result = result.filter(issue =>
        selectedProductValues.includes(issue.productName)
      );
    }

    // Filtrer pour n'afficher que les nouvelles issues si l'option est activée
    if (showOnlyNewIssues) {
      // Nous filtrons les issues dont la date de création est dans la période sélectionnée
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      result = result.filter(issue => {
        const issueDate = new Date(issue.date);
        return issueDate >= startDateObj && issueDate <= endDateObj;
      });
    }

    return result;
  }, [filteredIssues, selectedReasons, selectedProducts, showOnlyNewIssues, startDate, endDate]);

  // Effet pour créer le mapping des couleurs une fois les issues chargées
  useEffect(() => {
    if (!issues.length) return;

    // Récupérer toutes les raisons uniques et les trier par ordre alphabétique
    const uniqueReasons = [...new Set(issues.map(issue => issue.reason || 'Non catégorisé'))].sort();

    // Créer le mapping des couleurs
    const newMapping = uniqueReasons.reduce((acc, reason, index) => {
      const colorIndex = index % BASE_COLORS.backgrounds.length;
      acc[reason] = {
        background: BASE_COLORS.backgrounds[colorIndex],
        border: BASE_COLORS.borders[colorIndex],
        backgroundWithOpacity: BASE_COLORS.backgroundsWithOpacity[colorIndex]
      };
      return acc;
    }, {});

    setReasonColorMapping(newMapping);
  }, [issues]);

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      return 'Date inconnue';
    }
  };

  // Date sorting function
  const dateSortFunction = (a, b, direction) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);

    if (direction === 'ascending') {
      return dateA - dateB;
    } else {
      return dateB - dateA;
    }
  };

  // Define table columns
  const columns = [
    {
      key: 'interventionPoint',
      label: _t('Store')
    },
    {
      key: 'shopSign',
      label: _t('Shop sign')
    },
    {
      key: 'productName',
      label: _t('Product'),
      render: (item) => {
        return obfuscate ? `${item.productName.slice(0, 2)}${'*'.repeat(Math.max(0, item.productName.length - 2))}` : item.productName;
      }
    },
    {
      key: 'reason',
      label: _t('Reason'),
    },
    {
      key: 'reasonCategory',
      label: _t('Category'),
    },
    {
      key: 'date',
      label: _t('Visit date'),
      render: (item, forFilter) => {
        if (forFilter) return formatDate(item.date);
        return formatDate(item.date);
      },
      sortFunction: dateSortFunction
    },
    {
      key: 'daysInState',
      label: _t('Days in state'),
      render: (item) => `${item.daysInState} jour${item.daysInState > 1 ? 's' : ''}`
    }
  ];

  // Ne rendre les graphiques que si le mapping est prêt
  const renderCharts = issues.length > 0 && reasonColorMapping !== null;

  // Calcul des statistiques pour les indicateurs sur les données complètement filtrées
  const stats = useMemo(() => {
    if (!fullyFilteredIssues.length) return null;

    // Nombre de magasins distincts
    const uniqueStores = new Set(fullyFilteredIssues.map(issue => issue.interventionPoint));

    // Nombre de produits distincts
    const uniqueProducts = new Set(fullyFilteredIssues.map(issue => issue.productName));

    // Nombre de catégories distinctes
    const uniqueCategories = new Set(
      fullyFilteredIssues.map(issue => issue.reasonCategory).filter(Boolean)
    );

    // Moyenne des jours dans l'état
    const avgDays = fullyFilteredIssues.reduce((sum, issue) => sum + issue.daysInState, 0) / fullyFilteredIssues.length;

    return {
      issueCount: fullyFilteredIssues.length,
      storeCount: uniqueStores.size,
      productCount: uniqueProducts.size,
      categoryCount: uniqueCategories.size,
      avgDaysInState: avgDays
    };
  }, [fullyFilteredIssues]);

  const unFilteredIssuesStats = useMemo(() => {
    if (!issues.length) return null;

    // Nombre de magasins distincts
    const uniqueStores = new Set(issues.map(issue => issue.interventionPoint));

    // Nombre de produits distincts
    const uniqueProducts = new Set(issues.map(issue => issue.productName));

    // Nombre de catégories distinctes
    const uniqueCategories = new Set(
      issues.map(issue => issue.reasonCategory).filter(Boolean)
    );

    // Moyenne des jours dans l'état
    const avgDays = issues.reduce((sum, issue) => sum + issue.daysInState, 0) / issues.length;

    return {
      issueCount: issues.length,
      storeCount: uniqueStores.size,
      productCount: uniqueProducts.size,
      categoryCount: uniqueCategories.size,
      avgDaysInState: avgDays
    };
  }, [issues]);

  // Mémoisation des données pour l'analyse IA
  const analysisData = useMemo(() => {
    if (!unFilteredIssuesStats) return null;

    return {
      period: `${presetType}_${presetValue}`,
      currentPeriodStats: {
        issueCount: unFilteredIssuesStats.issueCount,
        storeCount: unFilteredIssuesStats.storeCount,
        productCount: unFilteredIssuesStats.productCount,
        categoryCount: unFilteredIssuesStats.categoryCount,
        avgDaysInState: unFilteredIssuesStats.avgDaysInState,
      },
      previousPeriodStats: issuesPreviousPeriodStats,
      topReasons: issues.reduce((acc, issue) => {
        const reason = issue.reason || 'Non catégorisé';
        acc[reason] = (acc[reason] || 0) + 1;
        return acc;
      }, {}),
      topProductsInError: issues.reduce((acc, issue) => {
        if (issue.productName) {
          acc[issue.productName] = (acc[issue.productName] || 0) + 1;
        }
        return acc;
      }, {}),
      topShopSignsInError: issues.reduce((acc, issue) => {
        if (issue.shopSign) {
          acc[issue.shopSign] = (acc[issue.shopSign] || 0) + 1;
        }
        return acc;
      }, {}),
      issuesEvolutionByDayAndReason: issues.reduce((acc, issue) => {
        try {
          // Normaliser la date en s'assurant qu'elle est bien parsée
          let dateObj;

          if (typeof issue.date === 'string') {
            // Essayer plusieurs formats de date courants
            if (issue.date.includes('T')) {
              // Format ISO complet YYYY-MM-DDTHH:mm:ss.sssZ
              dateObj = new Date(issue.date);
            } else if (issue.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
              // Format YYYY-MM-DD
              const [year, month, day] = issue.date.split('-');
              dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            } else if (issue.date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              // Format DD/MM/YYYY
              const [day, month, year] = issue.date.split('/');
              dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            } else {
              // Utiliser date-fns pour parser la date
              dateObj = new Date(issue.date);
            }
          } else if (issue.date instanceof Date) {
            dateObj = issue.date;
          } else {
            // Si la date n'est pas reconnue, utiliser une date par défaut
            console.warn('Format de date non reconnu:', issue.date);
            return acc;
          }

          // Vérifier si la date est valide
          if (isNaN(dateObj.getTime())) {
            console.warn('Date invalide:', issue.date);
            return acc;
          }

          // Formater la date au format YYYY-MM-DD en utilisant date-fns
          const day = format(dateObj, 'yyyy-MM-dd');
          const reason = issue.reason || 'Non catégorisé';

          if (!acc[day]) {
            acc[day] = {};
          }

          if (!acc[day][reason]) {
            acc[day][reason] = 0;
          }

          acc[day][reason] += 1;
          return acc;
        } catch (error) {
          console.error('Erreur de traitement de date:', error, issue.date);
          return acc;
        }
      }, {})
    };
  }, [unFilteredIssuesStats]);

  const handleReasonSelect = (stat, reason) => {
    const option = reasonOptions.find(opt => opt.value === reason);

    if (option) {
      // Check if the reason is already selected
      const isAlreadySelected = selectedReasons.some(r => r.value === reason);

      if (isAlreadySelected) {
        // If already selected, remove it
        setSelectedReasons(prev => prev.filter(p => p.value !== reason));
      } else {
        // If not selected, add it
        setSelectedReasons(prev => [...prev.filter(p => p.value !== reason), option]);
      }
    }
  }

  const handleProductSelect = (stat, product) => {
    const option = productOptions.find(opt => opt.value === product);

    if (option) {
      // Check if the product is already selected
      const isAlreadySelected = selectedProducts.some(p => p.value === product);

      if (isAlreadySelected) {
        // If already selected, remove it
        setSelectedProducts(prev => prev.filter(p => p.value !== product));
      } else {
        // If not selected, add it
        setSelectedProducts(prev => [...prev.filter(p => p.value !== product), option]);
      }
    }
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="text-red-500 dark:text-red-400">
          <p className="text-center font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  // Modification de la partie conditionnelle pour le filtrage vide
  if (fullyFilteredIssues.length === 0 && issues.length > 0) {
    const hasActiveFilters = selectedReasons.length > 0 || selectedProducts.length > 0 || showOnlyNewIssues;

    return (
      <div className="bg-white dark:bg-gray-800 sm:p-6 p-4 rounded-lg shadow transition-all duration-300 h-full flex flex-col items-center justify-center text-center py-16">
        <div className="text-blue-500 dark:text-blue-400 mb-6">
          <RiFilterOffLine className="w-20 h-20 mx-auto" />
        </div>
        <h3 className="text-xl font-medium mb-3 dark:text-white">
          Aucun problème trouvé avec les filtres actuels
        </h3>
        <p className="text-gray-600 dark:text-gray-400 max-w-lg mb-8">
          Cette combinaison de filtres ne correspond à aucun problème dans la période sélectionnée.
        </p>

        {/* Afficher les filtres actifs */}
        <div className="flex flex-wrap justify-center gap-3 max-w-xl mb-6">
          {selectedShopSigns && selectedShopSigns.length > 0 && (
            <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm">
              {selectedShopSigns.length} enseigne{selectedShopSigns.length > 1 ? 's' : ''} sélectionnée{selectedShopSigns.length > 1 ? 's' : ''}
            </div>
          )}
          {selectedCategories && selectedCategories.length > 0 && (
            <div className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm">
              {selectedCategories.length} catégorie{selectedCategories.length > 1 ? 's' : ''} sélectionnée{selectedCategories.length > 1 ? 's' : ''}
            </div>
          )}
          {selectedReasons.length > 0 && (
            <div className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-sm">
              {selectedReasons.length} raison{selectedReasons.length > 1 ? 's' : ''} sélectionnée{selectedReasons.length > 1 ? 's' : ''}
            </div>
          )}
          {selectedProducts.length > 0 && (
            <div className="px-3 py-1 bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 rounded-full text-sm">
              {selectedProducts.length} produit{selectedProducts.length > 1 ? 's' : ''} sélectionné{selectedProducts.length > 1 ? 's' : ''}
            </div>
          )}
          {showOnlyNewIssues && (
            <div className="px-3 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full text-sm">
              Nouvelles issues uniquement
            </div>
          )}
        </div>

        {/* Bouton de réinitialisation des filtres */}
        {hasActiveFilters && (
          <button
            onClick={() => {
              setSelectedReasons([]);
              setSelectedProducts([]);
              setShowOnlyNewIssues(false);
            }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors shadow-sm"
          >
            Réinitialiser les filtres avancés
          </button>
        )}
      </div>
    );
  }

  // Empty state complet (aucun problème, même sans filtres)
  if (issues.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 sm:p-6 p-4 rounded-lg shadow transition-all duration-300 h-full flex flex-col items-center justify-center text-center py-16">
        <div className="text-green-500 dark:text-green-400 mb-6">
          <RiCheckboxCircleLine className="w-20 h-20 mx-auto" />
        </div>
        <h3 className="text-xl font-medium mb-3 dark:text-white">
          Aucun problème identifié !
        </h3>
        <p className="text-gray-600 dark:text-gray-400 max-w-lg">
          Il n'y a aucun problème de distribution pour la période sélectionnée.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 sm:p-6 p-4 rounded-lg shadow transition-all duration-300 h-full flex flex-col">

      {stats && (
        <>
          <div className="flex justify-between items-start">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
              <StatTile
                label="Absences"
                value={stats.issueCount.toString()}
                previousValue={showOnlyNewIssues ? null : issuesPreviousPeriodStats?.totalIssues.toString()}
                icon={<RiAlertLine />}
                color="blue"
                size="s"
              />
              <StatTile
                label="Magasins concernés"
                value={stats.storeCount.toString()}
                previousValue={showOnlyNewIssues ? null : issuesPreviousPeriodStats?.uniqueShops.toString()}
                icon={<RiStore2Line />}
                color="blue"
                size="s"
              />
              <StatTile
                label="Produits concernés"
                value={stats.productCount.toString()}
                previousValue={showOnlyNewIssues ? null : issuesPreviousPeriodStats?.uniqueProducts.toString()}
                icon={<RiBox3Line />}
                color="emerald"
                size="s"
              />
              <StatTile
                label="Catégories concernées"
                value={stats.categoryCount.toString()}
                previousValue={showOnlyNewIssues ? null : issuesPreviousPeriodStats?.uniqueCategories.toString()}
                icon={<RiListCheck2 />}
                color="amber"
                size="s"
              />
            </div>

            {/* Nouveaux filtres */}
            <div className="hidden md:flex flex-col space-y-2 ml-4 w-64">
              <Select
                isMulti
                options={reasonOptions}
                value={selectedReasons}
                onChange={setSelectedReasons}
                placeholder={`${_t('Filter by reason...')} (${reasonOptions.length})`}
                noOptionsMessage={() => "Aucune raison trouvée"}
                className="text-xs"
                classNamePrefix="select"
                styles={selectStyles}
                isClearable={true}
                menuPortalTarget={document.body}
              />
              <Select
                isMulti
                options={productOptions}
                value={selectedProducts}
                onChange={setSelectedProducts}
                placeholder={`${_t('Filter by product...')} (${productOptions.length})`}
                noOptionsMessage={() => "Aucun produit trouvé"}
                className="text-xs"
                classNamePrefix="select"
                styles={selectStyles}
                isClearable={true}
                menuPortalTarget={document.body}
              />
              <div className="flex items-center mt-2 text-sm text-gray-600 dark:text-gray-400">
                <label className="inline-flex items-center cursor-pointer">
                  <span className="mr-2">{_t('All absences')}</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={showOnlyNewIssues}
                      onChange={() => setShowOnlyNewIssues(!showOnlyNewIssues)}
                    />
                    <div className={`block w-10 h-6 rounded-full ${showOnlyNewIssues ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-700'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${showOnlyNewIssues ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                  <span className="ml-2">{_t('New only')}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Version mobile des filtres - visible seulement sur petit écran */}
          <div className="md:hidden mb-4">
            <div className="grid grid-cols-1 gap-2">
              <details className="bg-gray-50 dark:bg-gray-700 rounded p-2 text-sm">
                <summary className="cursor-pointer flex items-center text-gray-600 dark:text-gray-300">
                  <RiFilterLine className="mr-2" />
                  {_t('Additional filters')} ({selectedReasons.length + selectedProducts.length + (showOnlyNewIssues ? 1 : 0)})
                </summary>
                <div className="mt-3 space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {_t('Reasons')}
                    </label>
                    <Select
                      isMulti
                      options={reasonOptions}
                      value={selectedReasons}
                      onChange={setSelectedReasons}
                      placeholder={_t('Filter by reason...')}
                      noOptionsMessage={() => _t('No reason found')}
                      className="text-xs"
                      classNamePrefix="select"
                      styles={selectStyles}
                      isClearable={true}
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {_t('Products')}
                    </label>
                    <Select
                      isMulti
                      options={productOptions}
                      value={selectedProducts}
                      onChange={setSelectedProducts}
                      placeholder={_t('Filter by product...')}
                      noOptionsMessage={() => _t('No product found')}
                      className="text-xs"
                      classNamePrefix="select"
                      styles={selectStyles}
                      isClearable={true}
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div className="pt-1">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {_t('Issues type')}
                    </label>
                    <div className="flex items-center pt-1 text-sm text-gray-600 dark:text-gray-400">
                      <label className="inline-flex items-center cursor-pointer">
                        <span className="mr-2 text-xs">{_t('All')}</span>
                        <div className="relative">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={showOnlyNewIssues}
                            onChange={() => setShowOnlyNewIssues(!showOnlyNewIssues)}
                          />
                          <div className={`block w-8 h-5 rounded-full ${showOnlyNewIssues ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-700'}`}></div>
                          <div className={`dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition ${showOnlyNewIssues ? 'transform translate-x-3' : ''}`}></div>
                        </div>
                        <span className="ml-2 text-xs">{_t('New only')}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </details>
            </div>
          </div>

          {(analysisData && `${presetType}_${presetValue}` === analysisData.period) && (
            <div className="mt-4">
              <IAAnalysis
                projectId={projectId}
                startDate={startDate}
                endDate={endDate}
                slug="dn-problems-analysis"
                presetType={presetType}
                presetValue={presetValue}
                fetchWithAuth={fetchWithAuth}
                currentUser={currentUser}
                data={analysisData}
                prompt={_t('Analyze the distribution problems for the selected period. We want to help identify if there are distribution issues specific to a product or a shop sign, if the problems decrease or increase over the period considered and in comparison to the previous period if the data is available. The analysis must be concise, maximum 300 characters and actionable, but avoid banalities or boilerplate phrases.')}
              />
            </div>
          )}
        </>
      )}

      <hr className="border-t border-gray-200 dark:border-gray-700 my-2 sm:my-8" />

      {renderCharts && (
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium mb-4 dark:text-white">{_t('Absences reasons')}</h3>
            <DNAbsenceReasons
              issues={fullyFilteredIssues}
              colorMapping={reasonColorMapping}
              onItemSelect={handleReasonSelect}
            />
          </div>
          <div>
            <h3 className="text-lg font-medium mb-4 dark:text-white">{_t('Time evolution')}</h3>
            <DNAbsenceReasonGraph
              issues={fullyFilteredIssues}
              colorMapping={reasonColorMapping}
              onItemSelect={handleReasonSelect}
            />
          </div>
        </div>
      )}

      {renderCharts && (
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium mb-4 dark:text-white">{_t('Shop sign distribution')}</h3>
            <DNAbsenceReasons
              issues={fullyFilteredIssues}
              colorMapping={SHOP_SIGN_COLORS}
              dataKey="shopSign"
              title={_t('Shop signs')}
              onItemSelect={(stat, shopSign) => {
                if (!selectedShopSigns.some(s => s.value === shopSign)) {
                  emitter.emit('selectedShopSign', { name: shopSign });
                }

                if (selectedShopSigns.some(s => s.value === shopSign)) {
                  emitter.emit('selectedShopSign', null);
                }
              }}
            />
          </div>
          <div>
            <h3 className="text-lg font-medium mb-4 dark:text-white">{_t('Product distribution')} {productOptions.length > 20 ? `(${_t('Top 20')})` : ''}</h3>
            <DNAbsenceReasons
              issues={fullyFilteredIssues}
              colorMapping={PRODUCT_COLORS}
              dataKey="productName"
              title={_t('Products')}
              limit={20}
              withOthers={false}
              onItemSelect={handleProductSelect}
              obfuscate={obfuscate}
            />
          </div>
        </div>
      )}

      <hr className="border-t border-gray-200 dark:border-gray-700 my-8 sm:my-12" />

      <h3 className="text-lg font-medium mb-4 dark:text-white">{_t('List of problems')}</h3>

      <SortableTable
        data={fullyFilteredIssues}
        columns={columns}
        emptyMessage={
          selectedReasons.length > 0 || selectedProducts.length > 0 || showOnlyNewIssues
            ? _t('No distribution problem detected for the selected filters')
            : _t('No distribution problem detected')
        }
        countLabel={`Total: ${fullyFilteredIssues.length} ${_n('identified problem', 'identified problems', fullyFilteredIssues.length)}`}
        exportable={true}
        exportFilename={`problemes-distribution`}
      />
    </div>
  );
}

export default DNErrorViewer;